import { supabase } from '@/lib/supabaseClient'

/**
 * Get the current user's buyer partnership
 */
export async function getMyPartnership() {
  const { data, error } = await supabase
    .rpc('get_my_buyer_partnership')

  if (error) throw error
  return data?.[0] || null
}

/**
 * Create a new buyer partnership invitation
 */
export async function createPartnershipInvitation(email) {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('Not authenticated')

  // Check if user already has a partnership
  const { data: canCreate, error: checkError } = await supabase
    .rpc('can_create_buyer_partnership')

  if (checkError) throw checkError
  if (!canCreate) {
    throw new Error('You already have a partnership')
  }

  // Check if invited email is an existing user
  const { data: profiles, error: profileError } = await supabase
    .from('profiles')
    .select('id, role')
    .eq('email', email)
    .maybeSingle()

  if (profileError) throw profileError

  // If user exists, verify they're a buyer
  if (profiles && profiles.role !== 'buyer') {
    throw new Error('Can only partner with other buyers')
  }

  // Cannot partner with yourself
  if (profiles && profiles.id === user.id) {
    throw new Error('Cannot partner with yourself')
  }

  // Check if invited user already has a partnership
  if (profiles) {
    const { data: existingPartnership, error: existingError } = await supabase
      .from('buyer_partnerships')
      .select('id')
      .or(`buyer1_id.eq.${profiles.id},buyer2_id.eq.${profiles.id}`)
      .maybeSingle()

    if (existingError) throw existingError
    if (existingPartnership) {
      throw new Error('This user already has a partnership')
    }
  }

  // Create partnership invitation
  const { data, error } = await supabase
    .from('buyer_partnerships')
    .insert({
      buyer1_id: user.id,
      buyer2_id: profiles?.id || null,
      invitation_email: email,
      status: profiles ? 'pending' : 'pending'
    })
    .select()
    .single()

  if (error) throw error
  return data
}

/**
 * Accept a partnership invitation
 */
export async function acceptPartnershipInvitation(partnershipId) {
  const { data, error } = await supabase
    .from('buyer_partnerships')
    .update({
      status: 'accepted',
      accepted_at: new Date().toISOString()
    })
    .eq('id', partnershipId)
    .select()
    .single()

  if (error) throw error
  return data
}

/**
 * Decline a partnership invitation
 */
export async function declinePartnershipInvitation(partnershipId) {
  const { data, error } = await supabase
    .from('buyer_partnerships')
    .update({
      status: 'declined'
    })
    .eq('id', partnershipId)
    .select()
    .single()

  if (error) throw error
  return data
}

/**
 * Remove/dissolve a buyer partnership
 */
export async function removePartnership(partnershipId) {
  const { error } = await supabase
    .from('buyer_partnerships')
    .delete()
    .eq('id', partnershipId)

  if (error) throw error
}

/**
 * Get combined follows from both partners
 */
export async function getPartnerFollows() {
  const { data, error } = await supabase
    .rpc('get_partner_follows')

  if (error) throw error
  return data || []
}

/**
 * Check for pending invitations for current user
 */
export async function checkPendingInvitations() {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return null

  const { data: profile } = await supabase
    .from('profiles')
    .select('email')
    .eq('id', user.id)
    .single()

  if (!profile) return null

  const { data, error } = await supabase
    .from('buyer_partnerships')
    .select(`
      *,
      initiator:profiles!buyer_partnerships_buyer1_id_fkey(full_name, email)
    `)
    .eq('invitation_email', profile.email)
    .eq('status', 'pending')
    .is('buyer2_id', null)
    .maybeSingle()

  if (error) throw error
  return data
}

/**
 * Claim a pending invitation (when user signs up with invited email)
 */
export async function claimPendingInvitation() {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('Not authenticated')

  const { data: profile } = await supabase
    .from('profiles')
    .select('email, role')
    .eq('id', user.id)
    .single()

  if (!profile) throw new Error('Profile not found')
  if (profile.role !== 'buyer') throw new Error('Must be a buyer')

  const { data, error } = await supabase
    .from('buyer_partnerships')
    .update({
      buyer2_id: user.id
    })
    .eq('invitation_email', profile.email)
    .eq('status', 'pending')
    .is('buyer2_id', null)
    .select()
    .maybeSingle()

  if (error) throw error
  return data
}
